import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, User, LogOut, LayoutDashboard, UserCircle, Settings } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ThemeToggle } from './ThemeToggle';
import { useAuth, usePublicConfig } from '../context';
import { useTheme } from '../hooks/useTheme';

const links = [
  { label: 'Overview', href: '/' },
  { label: 'Features', href: '/#features' },
  { label: 'Operations', href: '/#pricing' },
  { label: 'Testimonials', href: '/#faqs' },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { config: publicConfig } = usePublicConfig();

  const { theme } = useTheme();
  const isAuthed = isAuthenticated;
  const chamaName = publicConfig?.name ?? 'LoopFund';
  const displayName = user?.full_name ?? 'Member';
  
  // Use theme-aware logo
  const logoSrc = theme === 'dark' 
    ? '/logos/dark.png' 
    : '/logos/light.png';

  const displayInitial = useMemo(() => {
    if (user?.full_name) {
      const [firstName, lastName] = user.full_name.split(' ');
      if (firstName && lastName) {
        return `${firstName[0]}${lastName[0]}`.toUpperCase();
      }
      return firstName?.[0]?.toUpperCase() ?? user.full_name[0]?.toUpperCase() ?? 'M';
    }
    return 'M';
  }, [user?.full_name]);

  const toggle = () => setOpen((prev) => !prev);

  const close = () => setOpen(false);

  const toggleProfile = () => setProfileOpen((prev) => !prev);

  const handleSignOut = () => {
    setProfileOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Link
          to="/"
          className="hidden lg:inline-flex lg:items-center lg:gap-3 lg:font-display lg:text-lg lg:font-semibold lg:text-slate-900 lg:transition-colors lg:hover:text-brand-500 dark:lg:text-white"
        >
          <img 
            src={logoSrc} 
            alt={chamaName}
            className="h-10 w-auto object-contain"
          />
         
        </Link>

        <nav className="hidden items-center gap-8 text-sm font-medium text-slate-700 dark:text-slate-200 lg:flex">
          {!isAuthed &&
            links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative inline-flex items-center gap-1 transition-colors hover:text-brand-500"
              >
                {link.label}
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-1 left-0 h-0.5 w-full origin-left scale-x-0 bg-brand-500 transition-transform duration-200 group-hover:scale-x-100"
                />
              </a>
            ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {isAuthed ? (
            <div className="relative">
              <button
                onClick={toggleProfile}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-brand-400 hover:text-brand-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-brand-300"
              >
                <User className="h-4 w-4" />
                {displayName}
              </button>
              <AnimatePresence>
                {profileOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="absolute right-0 mt-3 w-56 rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900"
                  >
                    <Link
                      to="/dashboard"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                    <button
                      className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-slate-100 dark:hover:bg-slate-800"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/profile');
                      }}
                    >
                      <UserCircle className="h-4 w-4" />
                      Profile
                    </button>
                    <button
                      className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-slate-100 dark:hover:bg-slate-800"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/settings');
                      }}
                    >
                      <Settings className="h-4 w-4" />
                      Settings
                    </button>
                    <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
                    <button
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-red-600 transition hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
                      onClick={handleSignOut}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          ) : (
            <>
              <Link
                to="/apply"
                className="text-sm font-medium text-slate-600 transition-colors hover:text-brand-500 dark:text-slate-300"
              >
                Apply
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
              >
                Sign in
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
          <ThemeToggle />
        </div>

        <div className="flex w-full items-center justify-between gap-3 lg:hidden">
          <button
            aria-label="Toggle navigation"
            onClick={toggle}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <Link
            to="/"
            className="inline-flex flex-1 items-center justify-center gap-2 font-display text-lg font-semibold text-slate-900 transition-colors hover:text-brand-500 dark:text-white"
          >
            {chamaName}
          </Link>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            {isAuthed ? (
              <Link
                to="/dashboard"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold uppercase shadow-sm transition hover:border-brand-400 hover:text-brand-600 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-brand-300"
              >
                {displayInitial}
              </Link>
            ) : (
              <Link
                to="/login"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <User className="h-5 w-5 text-brand-600 dark:text-brand-300" />
              </Link>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden"
            onClick={close}
          >
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="relative h-full w-5/6 max-w-xs bg-white p-6 shadow-xl dark:bg-slate-900"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <Link to="/" onClick={close} className="inline-flex items-center gap-3 font-display text-lg text-slate-900 dark:text-white">
                  <img 
                    src={logoSrc} 
                    alt={chamaName}
                    className="h-9 w-auto object-contain"
                  />
                
                </Link>
                <button
                  aria-label="Close navigation"
                  onClick={close}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 space-y-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                {!isAuthed &&
                  links.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={close}
                      className="block rounded-xl px-3 py-3 transition-colors duration-200 hover:bg-slate-100 hover:text-brand-600 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      {link.label}
                    </a>
                  ))}
              </div>

              <div className="mt-6 space-y-3 text-sm">
                {isAuthed ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Settings
                    </Link>
                    <Link
                      to="/dashboard/contributions"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Contributions
                    </Link>
                    <Link
                      to="/dashboard/payouts"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Payouts
                    </Link>
                    <Link
                      to="/dashboard/support"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Support
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/apply"
                      onClick={close}
                      className="block rounded-xl px-3 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:text-slate-200 dark:hover:bg-slate-800/70 dark:hover:text-brand-200"
                    >
                      Apply
                    </Link>
                    <Link
                      to="/login"
                      onClick={close}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-3 font-semibold text-brand-600 transition-colors hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-slate-800/60"
                    >
                      Sign in
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </>
                )}
              </div>

              {isAuthed ? (
                <button
                  onClick={() => {
                    handleSignOut();
                    close();
                  }}
                  className="absolute inset-x-0 bottom-0 flex items-center gap-2 border-t border-slate-200 px-6 py-4 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:border-slate-700 dark:text-red-300 dark:hover:bg-red-500/10"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              ) : null}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};

